import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const counterTickUpSkill: Skill = {
    name: SKILL_NAMES.counterTickUp,
    kind: 'codeo',
    description:
        'Animate a number counting up to its final value, landing exactly when the value is ' +
        'spoken — for stats, prices, percentages, and any hero number the voiceover delivers.',
    instructions: `# Counter Tick-Up

A hero number ticks from 0 (or a chosen floor) to its final value, and the tick *lands* exactly on the number's spoken onset. The landing-on-the-word is the point — a counter that finishes early or late reads worse than no counter.

## Markup

Keep the ticking digits in their own span so a static prefix/suffix ("$", "%", "/mo") doesn't reflow while the value changes. Use tabular figures so digit changes don't jitter the layout.

\`\`\`html
<div id="stat" style="position: absolute; left: 160px; top: 420px; font-family: var(--l5-font-0), sans-serif; font-size: 160px; font-weight: 800; color: var(--l5-color-0, #101820); font-variant-numeric: tabular-nums;">
  <span>$</span><span id="stat-value">0</span>
</div>
\`\`\`

## Timeline code

Let \`ONSET\` be the number's spoken second (read straight off the scene's \`cues:\` line in the video state, where each word is prefixed \`[N.Ns]\`) and \`TICK = 0.9\` the tick duration. Start the tick at \`ONSET - TICK\` so the final value lands on the word.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ONSET = 1.3;
const TICK = 0.9;
const statValueEl = document.querySelector('#stat-value');
const counter = { value: 0 };
tl.fromTo('#stat', { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, ONSET - TICK - 0.2);
tl.to(counter, {
    value: 18000,
    duration: TICK,
    ease: 'power1.out',
    onUpdate: () => {
        statValueEl.textContent = Math.round(counter.value).toLocaleString('en-US');
    },
}, ONSET - TICK);
window.__timelines = { main: tl };
\`\`\`

This is seek-safe: GSAP re-runs \`onUpdate\` with the interpolated value on every seek, so scrubbing and rendering stay deterministic. (Interpolate from the timeline like this — never derive the value from \`Date.now()\` or a real clock.)

## Adapt

- \`value\`/formatting: use \`toLocaleString\` for thousands separators; \`counter.value.toFixed(1)\` for decimals (then tween a float).
- \`ease: 'power1.out'\` decelerates into the final value so the landing feels deliberate. \`'power2.out'\` for a snappier arrival.
- A landing punch is optional: a small \`back.out(1.4)\` scale pulse on \`#stat\` AT \`ONSET\` (duration ~0.3) makes the number land hard. One per scene at most.

## Pitfalls

- Don't tick longer than ~1.2s — a long grind reads as a loading screen.
- Don't put the "$"/"%" inside the ticking span; it must not move.
- Without \`font-variant-numeric: tabular-nums\` (or a monospace digit font) the layout jitters every frame.
- The counter element is GSAP-tweened (opacity/y) — never bake a \`transform\` into its CSS.
- The entrance \`fromTo\` starts from \`opacity: 0\`, so default \`immediateRender\` keeps it hidden until it enters — don't add \`immediateRender: false\`.
- Every timeline position must be ≥ 0. Keep \`ONSET\` at least \`TICK\` + the entrance's lead-in (here 0.2) so \`ONSET - TICK - 0.2\` doesn't land at a negative position — a negative insertion time is not a valid point on the timeline.
- A hero number this size clears the readable floor easily; keep any secondary label above ~2.5% of canvas height too.`,
};
